import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { CheckCircleIcon, ArrowPathIcon } from '@heroicons/react/24/outline';

interface HierarchyBehaviorFieldProps {
  fieldKey: string;
  fieldData: {
    value: {
      mode: string;
      enable_promotions: boolean;
      enable_demotions: boolean;
      enable_swaps: boolean;
      cross_broker_threshold: number;
    };
    default_value: {
      mode: string;
      enable_promotions: boolean;
      enable_demotions: boolean;
      enable_swaps: boolean;
      cross_broker_threshold: number;
    };
    description: string;
    validation_rules: any;
    has_user_override: boolean;
  };
  onValueChange: (path: string, value: any) => void;
  onRemoveOverride: (path: string) => void;
  categoryKey: string;
}

export const HierarchyBehaviorField: React.FC<HierarchyBehaviorFieldProps> = ({
  fieldKey,
  fieldData,
  onValueChange,
  onRemoveOverride,
  categoryKey
}) => {
  const { value, default_value, description, has_user_override } = fieldData;
  
  const [localThreshold, setLocalThreshold] = useState<string>('');
  const [isThresholdFocused, setIsThresholdFocused] = useState(false);

  useEffect(() => {
    if (!isThresholdFocused) {
      setLocalThreshold('');
    }
  }, [fieldKey, has_user_override, isThresholdFocused]);

  const formatLabel = (key: string) => {
    return key
      .replace(/_/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const updateField = (fieldName: string, newValue: any) => {
    const newConfig = { ...value, [fieldName]: newValue };
    onValueChange(`${categoryKey}.${fieldKey}`, newConfig);
  };

  const modeOptions = [
    {
      value: 'same_broker_only',
      label: 'Same Broker Only',
      description: 'Only promote/demote within the same exchange (e.g., Bybit Account1 → Bybit Account2)'
    },
    {
      value: 'cross_broker_allowed',
      label: 'Cross-Broker Allowed',
      description: 'Allow moves between any accounts, including different exchanges'
    },
    {
      value: 'disabled',
      label: 'Disabled',
      description: 'No account moves - strategies stay in their current accounts'
    }
  ];

  const currentMode = modeOptions.find(opt => opt.value === value.mode);
  const showThresholdField = value.mode === 'cross_broker_allowed';

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className="bg-white/5 rounded-lg p-4 border border-white/10 space-y-4"
    >
      {/* Field Header */}
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <h4 className={`text-base font-medium mb-1 ${has_user_override ? 'text-green-400' : 'text-white/90'}`}>
            Account Hierarchy Behavior
            {has_user_override && (
              <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-400 border border-green-500/30">
                <CheckCircleIcon className="w-3 h-3 mr-1" />
                Customized
              </span>
            )}
          </h4>
          <p className="text-sm text-white/70 leading-relaxed break-words whitespace-pre-wrap">
            {description}
          </p>
        </div>
        
        {has_user_override && (
          <div className="flex items-center space-x-2 ml-4">
            <button
              onClick={() => onRemoveOverride(`${categoryKey}.${fieldKey}`)}
              className="p-1.5 text-white/60 hover:text-red-400 transition-colors bg-white/5 hover:bg-red-500/10 rounded-md border border-white/10 hover:border-red-500/30"
              title="Reset to default values"
            >
              <ArrowPathIcon className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {/* Configuration Fields */}
      <div className="space-y-4 pt-2">
        
        {/* Mode Selection */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-white/90">
            Account Move Behavior
            <span className="ml-2 text-xs text-white/50">
              (default: {formatLabel(default_value.mode)})
            </span>
          </label>
          <div className="space-y-2">
            {modeOptions.map((option) => (
              <label key={option.value} className="flex items-start space-x-3 cursor-pointer">
                <input
                  type="radio"
                  name="hierarchy_mode"
                  value={option.value}
                  checked={value.mode === option.value}
                  onChange={(e) => updateField('mode', e.target.value)}
                  className="mt-1 w-4 h-4 text-blue-600 bg-white/10 border-white/30 focus:ring-blue-500 focus:ring-2"
                />
                <div className="flex-1">
                  <div className="text-sm font-medium text-white/90">{option.label}</div>
                  <div className="text-xs text-white/60">{option.description}</div>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Enable/Disable Controls - Only show when mode is not disabled */}
        {value.mode !== 'disabled' && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-2">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={value.enable_promotions}
                  onChange={(e) => updateField('enable_promotions', e.target.checked)}
                  className="w-4 h-4 text-blue-600 bg-white/10 border-white/30 rounded focus:ring-blue-500 focus:ring-2"
                />
                <span className="text-sm font-medium text-white/90">Enable Promotions</span>
              </label>
              <p className="text-xs text-white/60 ml-6">
                Allow strategies to move to higher-tier accounts
              </p>
            </div>

            <div className="space-y-2">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={value.enable_demotions}
                  onChange={(e) => updateField('enable_demotions', e.target.checked)}
                  className="w-4 h-4 text-blue-600 bg-white/10 border-white/30 rounded focus:ring-blue-500 focus:ring-2"
                />
                <span className="text-sm font-medium text-white/90">Enable Demotions</span>
              </label>
              <p className="text-xs text-white/60 ml-6">
                Allow strategies to move to lower-tier accounts
              </p>
            </div>

            <div className="space-y-2">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={value.enable_swaps}
                  onChange={(e) => updateField('enable_swaps', e.target.checked)}
                  className="w-4 h-4 text-blue-600 bg-white/10 border-white/30 rounded focus:ring-blue-500 focus:ring-2"
                />
                <span className="text-sm font-medium text-white/90">Enable Swaps</span>
              </label>
              <p className="text-xs text-white/60 ml-6">
                Allow strategies to swap between accounts
              </p>
            </div>
          </div>
        )}

        {/* Show explanation when mode is disabled */}
        {value.mode === 'disabled' && (
          <div className="bg-gray-500/20 rounded-lg p-3 border border-gray-400/30">
            <div className="flex items-center space-x-2">
              <span className="text-gray-400">ℹ️</span>
              <span className="text-sm text-gray-300 font-medium">Account Moves Disabled</span>
            </div>
            <p className="text-xs text-gray-400 mt-1">
              When account hierarchy behavior is disabled, all strategies will remain in their current accounts. 
              Promotion and demotion settings are not applicable in this mode.
            </p>
          </div>
        )}

        {/* Cross-Broker Threshold (only when cross-broker is allowed) */}
        {showThresholdField && (
          <div className="space-y-2">
            <label className="block text-sm font-medium text-white/90">
              Cross-Broker Threshold
              <span className="ml-2 text-xs text-white/50">
                (default: {default_value.cross_broker_threshold}% ROT)
              </span>
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="number"
                value={isThresholdFocused && localThreshold !== '' ? localThreshold : value.cross_broker_threshold}
                onChange={(e) => {
                  const inputValue = e.target.value;
                  if (isThresholdFocused) {
                    setLocalThreshold(inputValue);
                  }
                  const numValue = inputValue === '' || inputValue === null ? null : parseFloat(inputValue);
                  updateField('cross_broker_threshold', numValue);
                }}
                onFocus={() => {
                  setIsThresholdFocused(true);
                  setLocalThreshold(value.cross_broker_threshold?.toString() || '');
                }}
                onBlur={() => {
                  setIsThresholdFocused(false);
                  setLocalThreshold('');
                }}
                min={0}
                max={10}
                step={0.1}
                className="flex-1 px-3 py-2 bg-white/5 border border-white/20 rounded-md text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="2.0"
              />
              <span className="text-sm text-white/60 min-w-fit">ROT</span>
            </div>
            <p className="text-xs text-white/60">
              Additional performance threshold required for moves between different exchanges
            </p>
          </div>
        )}

        {/* Configuration Summary */}
        <div className="bg-white/5 rounded-lg p-3 border border-white/10">
          <h5 className="text-sm font-medium text-white/90 mb-2">Current Configuration:</h5>
          <div className="text-xs text-white/70 space-y-1">
            <div>• Account moves: <span className="text-blue-400">{currentMode?.label}</span></div>
            {value.mode !== 'disabled' && (
              <>
                <div>• Promotions: <span className={value.enable_promotions ? "text-green-400" : "text-red-400"}>
                  {value.enable_promotions ? "Enabled" : "Disabled"}
                </span></div>
                <div>• Demotions: <span className={value.enable_demotions ? "text-green-400" : "text-red-400"}>
                  {value.enable_demotions ? "Enabled" : "Disabled"}
                </span></div>
                <div>• Swaps: <span className={value.enable_swaps ? "text-green-400" : "text-red-400"}>
                  {value.enable_swaps ? "Enabled" : "Disabled"}
                </span></div>
              </>
            )}
            {value.mode === 'disabled' && (
              <div>• All account moves are disabled - strategies remain in current accounts</div>
            )}
            {showThresholdField && (
              <div>• Cross-broker threshold: <span className="text-yellow-400">{value.cross_broker_threshold}% ROT</span></div>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};
